/**
 * <TutorSoundModeToggle> — Dialogue ou Concentré, incrusté en bas à droite de la vidéo d'un élève
 * (molecule).
 *
 * Weekly du 03/11 (W5) : la bascule quitte le dessous de la vidéo, où elle prenait toute la largeur de la
 * carte, pour un seul bouton posé dans la visio. Le mode se lit d'un coup d'œil, par le glyphe et l'aplat (le
 * haut-parleur sur le vert de progression pour Dialogue, le son coupé sur le violet du live pour Concentré, les
 * glyphes et les couleurs de l'ancienne bascule, `LiveParticipant.js:236-266`) ; un toucher passe à l'autre mode.
 * La bascule qui part se voit : le bouton tourne, sans répondre, jusqu'à ce que l'écran pose le nouveau mode.
 *
 * Test du 04/11 après-midi (Wajih) : **un vrai bouton-poussoir du design system** (`<PushButton iconOnly>`), son
 * relief dans le ton foncé de son aplat, pressé il descend. La pastille d'avant ne se lisait pas comme un bouton.
 *
 * L'élève parti : le bouton se lit, estompé, et ne se touche pas. Contrôlé : le mode arrive en prop.
 */
import React from "react"

import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { actionFace, PushButton, pushButtonFaceHeight, type ButtonSize } from "../../atoms/Buttons/Button/Button"
import { slopTo } from "../../utils/touchTarget"
import { SOUND_MODE_LABELS } from "../SoundModeBadge"
import { SOUND_MODE_FACE } from "../TutorSoundModeSwitch/TutorSoundModeSwitch"

export type TutorSoundModeToggleProps = {
    mode: UISoundMode
    onChange: (mode: UISoundMode) => void
    /** L'élève est parti : le bouton se lit, ne se touche pas. Défaut `false`. */
    disabled?: boolean
    /** La bascule part : le bouton tourne et ne répond plus. Défaut `false`. */
    pending?: boolean
    /** Le prénom de l'élève, pour le lecteur d'écran. */
    studentName: string
    /** `sm` (32, défaut) : une vignette de la pellicule ; `md` (44) : une case de la mosaïque. */
    size?: ButtonSize
    testID?: string
}

const OTHER: Record<UISoundMode, UISoundMode> = { dialogue: "focused", focused: "dialogue" }

/**
 * La face du poussoir, celle du principal de `<Button>` sur l'aplat du mode : le vert de progression pour Dialogue, le
 * violet du live pour Concentré, leur relief et leur encre lus sur leur descripteur (`fills`). La paire segmentée
 * (`SOUND_MODE_FACE`) garde la tinte du Dialogue, qui n'a pas de relief.
 */
const MODE_FACE = { dialogue: actionFace("solid", "success"), focused: actionFace("solid", "purple") }

/** Ce que dit le bouton : le mode posé, puis ce que fait le toucher. */
export function tutorSoundModeToggleLabel(mode: UISoundMode, studentName: string): string {
    return `${studentName} en ${SOUND_MODE_LABELS[mode]}. Passer en ${SOUND_MODE_LABELS[OTHER[mode]]}`
}

export function TutorSoundModeToggle(props: TutorSoundModeToggleProps) {
    const { mode, onChange, disabled = false, pending = false, studentName, size = "sm", testID } = props
    return (
        <PushButton
            iconOnly
            label={SOUND_MODE_LABELS[mode]}
            iconName={SOUND_MODE_FACE[mode].icon}
            face={MODE_FACE[mode]}
            size={size}
            disabled={disabled}
            loading={pending}
            // Un bouton de 32 px dans la vidéo : la cible s'étend à celle d'un doigt (tirée de la face : 44 de large), pas le
            // toucher de la vidéo.
            hitSlop={slopTo(pushButtonFaceHeight(size))}
            onPress={() => onChange(OTHER[mode])}
            accessibilityLabel={tutorSoundModeToggleLabel(mode, studentName)}
            testID={testID}
        />
    )
}
